/**
 * Vanity-host detection — "is the current host a custom domain mapped to
 * a tenant, and if so which slug?" Drives whether <VanityRouter> mounts
 * the slug-hiding history (see vanityHistory.ts) or a plain BrowserRouter.
 *
 * Resolution is async (a `/public/brand-from-host` round-trip) but the
 * router needs an answer at first paint, so we cache POSITIVE results in
 * localStorage keyed by host. A domain→tenant mapping is effectively
 * permanent (it only changes when a super-admin re-points the domain),
 * so a durable cache is safe and removes the cold-start flash on every
 * visit after the first.
 *
 * Cache value convention (localStorage `vanity_host_slug:<host>`):
 *   absent      → unresolved (caller must resolve)
 *   ''          → known default host (no vanity; plain BrowserRouter)
 *   '<slug>'    → vanity host for that tenant slug
 *
 * Negative network results are deliberately NOT cached, so a transient
 * backend hiccup can't pin a real vanity domain to the default brand
 * until the cache is cleared.
 */

import { resolveLoginSlugForHost } from './tenantResolver';

const CACHE_PREFIX = 'vanity_host_slug:';

/**
 * Hosts that can never be a customer vanity domain — the local dev
 * server and the platform's own PaaS hostnames. Short-circuited so the
 * default product paths never pay a resolution round-trip.
 */
export function isKnownDefaultHost(host: string): boolean {
  const h = (host.split(':')[0] || '').toLowerCase();
  return (
    h === '' ||  // no host (SSR / non-browser) → never a vanity domain
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h.endsWith('.local') ||
    h.endsWith('.run.app') ||
    h.endsWith('.vercel.app') ||
    h.endsWith('.netlify.app')
  );
}

/** Synchronous cache read. `undefined` = unresolved, `null` = default
 *  host, `string` = vanity slug. */
export function getCachedHostSlug(host: string): string | null | undefined {
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + host);
    if (raw === null) return undefined;
    return raw === '' ? null : raw;
  } catch {
    return undefined;
  }
}

function setCachedHostSlug(host: string, slug: string | null): void {
  try {
    window.localStorage.setItem(CACHE_PREFIX + host, slug ?? '');
  } catch {
    /* localStorage can throw under sandbox modes — non-fatal */
  }
}

/**
 * Resolve the tenant slug a host maps to, consulting (then populating)
 * the cache. Returns `null` for default hosts. Only positive results
 * are persisted (see module note).
 */
export async function resolveHostSlug(host: string): Promise<string | null> {
  const cached = getCachedHostSlug(host);
  if (cached !== undefined) return cached;
  if (isKnownDefaultHost(host)) {
    setCachedHostSlug(host, null);
    return null;
  }
  const slug = await resolveLoginSlugForHost(host);
  if (slug) setCachedHostSlug(host, slug);
  return slug;
}
